// services/VoiceAIService.ts
import type { VoiceServiceConfig, LiveAPIMessage } from '../types';
import { GoogleGenerativeAI } from '@google/generative-ai';
import OpenAI from 'openai';

// Define specific event listener types
type LiveAPIEventMap = {
  connected: () => void;
  disconnected: () => void;
  textResponse: (text: string) => void;
  audioResponse: (audioData: { data: string; mimeType: string }) => void;
  transcription: (transcript: string) => void;
  error: (error: Error) => void;
  turnComplete: () => void;
  toolCall: (toolCall: unknown) => void;
};

export class GeminiLiveAPIClient {
  private ws: WebSocket | null = null;
  private apiKey: string;
  private url: string;
  private listeners: Map<keyof LiveAPIEventMap, Set<LiveAPIEventMap[keyof LiveAPIEventMap]>> = new Map();
  private isConnected: boolean = false;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    this.url = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent?key=${apiKey}`;
  }

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          console.log('Gemini Live API connected');
          this.isConnected = true;
          this.emit('connected');
          this.sendSetup();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data) as LiveAPIMessage;
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        };

        this.ws.onclose = (event) => {
          console.log('Gemini Live API disconnected:', event.reason);
          this.isConnected = false;
          this.emit('disconnected');
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.emit('error', new Error(error.toString()));
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private sendSetup() {
    const setupMessage = {
      setup: {
        model: "models/gemini-2.0-flash-exp",
        generationConfig: {
          responseModalities: ["AUDIO", "TEXT"],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                // Use "Aoede" - better English voice
                voiceName: "Aoede"
              }
            }
          }
        },
        systemInstruction: {
          parts: [{
            text: "You are a helpful AI assistant. Respond naturally and conversationally in English. Keep responses concise but informative. Always respond in English language."
          }]
        }
      }
    };

    console.log('Setting up Live API with voice: Aoede');
    this.send(setupMessage);
  }

  private handleMessage(data: LiveAPIMessage) {
    if (data.serverContent) {
      const { serverContent } = data;

      if (serverContent.modelTurn) {
        const { parts } = serverContent.modelTurn;
        let textResponse = '';
        let audioData: { data: string; mimeType: string } | null = null;

        parts.forEach((part) => {
          if (part.text) {
            textResponse += part.text;
          }
          if (part.inlineData) {
            audioData = part.inlineData;
          }
        });

        if (textResponse) {
          this.emit('textResponse', textResponse);
        }
        if (audioData) {
          this.emit('audioResponse', audioData);
        }
      }

      if (serverContent.inputTranscription) {
        this.emit('transcription', serverContent.inputTranscription.text);
      }

      if (serverContent.turnComplete) {
        this.emit('turnComplete');
      }
    }

    if (data.toolCall) {
      this.emit('toolCall', data.toolCall);
    }
  }

  sendText(text: string) {
    if (!this.isConnected || !this.ws) {
      throw new Error('Not connected to Live API');
    }

    const message = {
      clientContent: {
        turns: [{
          role: "user",
          parts: [{ text }]
        }],
        turnComplete: true
      }
    };

    this.send(message);
  }

  sendAudio(audioData: ArrayBuffer) {
    if (!this.isConnected || !this.ws) {
      throw new Error('Not connected to Live API');
    }

    const base64Audio = btoa(String.fromCharCode(...new Uint8Array(audioData)));

    const message = {
      realtimeInput: {
        mediaChunks: [{
          mimeType: "audio/pcm;rate=16000",
          data: base64Audio
        }]
      }
    };

    this.send(message);
  }

  private send(message: Record<string, unknown>) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  // Type-safe event listeners
  on<K extends keyof LiveAPIEventMap>(event: K, callback: LiveAPIEventMap[K]) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    (this.listeners.get(event) as Set<LiveAPIEventMap[K]>).add(callback);
  }

  off<K extends keyof LiveAPIEventMap>(event: K, callback: LiveAPIEventMap[K]) {
    const eventListeners = this.listeners.get(event) as Set<LiveAPIEventMap[K]> | undefined;
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  private emit<K extends keyof LiveAPIEventMap>(event: K, ...args: Parameters<LiveAPIEventMap[K]>) {
    const eventListeners = this.listeners.get(event) as Set<LiveAPIEventMap[K]> | undefined;
    if (eventListeners) {
      eventListeners.forEach(callback => {
        (callback as (...args: Parameters<LiveAPIEventMap[K]>) => void)(...args);
      });
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }
}

export class VoiceAIService {
  private openaiClient?: OpenAI;
  private geminiClient?: GoogleGenerativeAI;
  private liveAPIClient?: GeminiLiveAPIClient;
  private provider: 'openai' | 'gemini';
  private recognition?: SpeechRecognition;
  private synthesis: SpeechSynthesis;
  private audioContext?: AudioContext;
  private currentUtterance?: SpeechSynthesisUtterance;

  constructor(config: VoiceServiceConfig) {
    this.provider = config.provider;
    this.synthesis = window.speechSynthesis;

    // Initialize Web Speech API with proper cross-browser support
    if (typeof window !== 'undefined') {
      const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognitionClass) {
        this.recognition = new SpeechRecognitionClass();
        this.recognition.continuous = false;
        this.recognition.interimResults = false;
        this.recognition.lang = 'en-US';
      }
    }

    // Initialize clients
    if (config.apiConfig.openai.apiKey) {
      this.openaiClient = new OpenAI({
        apiKey: config.apiConfig.openai.apiKey,
        dangerouslyAllowBrowser: true
      });
      console.log('OpenAI client initialized');
    }

    if (config.apiConfig.gemini.apiKey) {
      this.geminiClient = new GoogleGenerativeAI(config.apiConfig.gemini.apiKey);
      this.liveAPIClient = new GeminiLiveAPIClient(config.apiConfig.gemini.apiKey);
      console.log('Gemini client initialized with English voice');
    }
  }

  async connectLiveAPI(): Promise<void> {
    if (this.liveAPIClient && this.provider === 'gemini') {
      await this.liveAPIClient.connect();
    }
  }

  disconnectLiveAPI() {
    if (this.liveAPIClient) {
      this.liveAPIClient.disconnect();
    }
  }

  getLiveAPIClient(): GeminiLiveAPIClient | undefined {
    return this.liveAPIClient;
  }

  // Check if Live API is connected and should handle voice
  isUsingLiveAPI(): boolean {
    return this.provider === 'gemini' &&
      this.liveAPIClient?.getConnectionStatus() === true;
  }

  async sendTextMessage(message: string): Promise<string> {
    try {
      if (this.provider === 'openai') {
        return await this.sendOpenAIMessage(message);
      } else {
        // Use Live API if connected, otherwise fallback to regular API
        if (this.isUsingLiveAPI()) {
          this.liveAPIClient!.sendText(message);
          return ""; // Response will come through Live API events
        } else {
          return await this.sendGeminiMessage(message);
        }
      }
    } catch (error) {
      console.error('API Error:', error);
      throw new Error(`Failed to get ${this.provider.toUpperCase()} response: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async sendOpenAIMessage(message: string): Promise<string> {
    if (!this.openaiClient) {
      throw new Error('OpenAI client not initialized');
    }

    const completion = await this.openaiClient.chat.completions.create({
      messages: [{ role: 'user', content: message }],
      model: 'gpt-4o-mini',
      max_tokens: 150,
      temperature: 0.7
    });

    return completion.choices[0]?.message?.content || 'No response generated';
  }

  private async sendGeminiMessage(message: string): Promise<string> {
    if (!this.geminiClient) {
      throw new Error('Gemini client not initialized');
    }

    const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.0-flash-exp' });
    const result = await model.generateContent(message);

    return result.response.text() || 'No response generated';
  }

  async processVoiceInput(audioBlob: Blob): Promise<string> {
    // If using Gemini Live API, send audio directly
    if (this.isUsingLiveAPI()) {
      const arrayBuffer = await audioBlob.arrayBuffer();
      this.liveAPIClient!.sendAudio(arrayBuffer);
      return ""; // Response will come through Live API events
    }

    // Fallback to Web Speech API for non-Live API scenarios
    return new Promise((resolve, reject) => {
      if (!this.recognition) {
        // Fallback transcription for testing
        setTimeout(() => {
          resolve("Mock voice transcription: Hello, this is a test message");
        }, 1000);
        return;
      }

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        resolve(transcript);
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        reject(new Error(`Speech recognition error: ${event.error}`));
      };

      this.recognition.onend = () => {
        setTimeout(() => {
          resolve("Could not transcribe audio clearly");
        }, 100);
      };

      try {
        this.recognition.start();
      } catch {
        setTimeout(() => {
          resolve("Mock voice transcription: Hello from fallback");
        }, 1000);
      }
    });
  }

  async generateVoiceResponse(text: string): Promise<string> {
    // Skip speech synthesis if using Live API (it handles audio natively)
    if (this.isUsingLiveAPI()) {
      console.log('Live API handles voice responses natively, skipping Web Speech API');
      return "live-api-handled";
    }

    return new Promise((resolve) => {
      if (!this.synthesis) {
        resolve("data:audio/wav;base64,mock-audio-data");
        return;
      }

      // FIXED: Enhanced Chrome compatibility and error handling
      const initializeSpeech = () => {
        // Stop any existing speech first
        this.stopSpeech();

        // Strip markdown formatting from text for better speech
        const cleanText = text
          .replace(/\*\*\*([^*]+)\*\*\*/g, '$1') // Remove bold formatting
          .replace(/\*\*([^*]+)\*\*/g, '$1')
          .replace(/\*([^*]+)\*/g, '$1') // Remove italic formatting
          .replace(/\n+/g, ' ') // Replace newlines with spaces
          .trim();

        if (!cleanText) {
          resolve("empty-text");
          return;
        }

        const utterance = new SpeechSynthesisUtterance(cleanText);

        // Wait for voices to load with timeout
        const waitForVoices = (maxAttempts = 10) => {
          let attempts = 0;

          const checkVoices = () => {
            const voices = this.synthesis.getVoices();
            attempts++;

            if (voices.length > 0 || attempts >= maxAttempts) {
              setupUtteranceAndSpeak(voices);
            } else {
              setTimeout(checkVoices, 100);
            }
          };

          checkVoices();
        };

        const setupUtteranceAndSpeak = (voices: SpeechSynthesisVoice[]) => {
          // FIXED: Enhanced voice selection for better compatibility
          let selectedVoice: SpeechSynthesisVoice | null = null;

          if (voices.length > 0) {
            // Priority order for voice selection
            const voicePriorities = [
              // English native voices (usually most reliable)
              (v: SpeechSynthesisVoice) => v.localService && v.lang.startsWith('en-US'),
              (v: SpeechSynthesisVoice) => v.localService && v.lang.startsWith('en-'),
              // Google remote voices (high quality)
              (v: SpeechSynthesisVoice) => !v.localService && v.lang.startsWith('en-US'),
              (v: SpeechSynthesisVoice) => !v.localService && v.lang.startsWith('en-'),
              // Any English voice
              (v: SpeechSynthesisVoice) => v.lang.startsWith('en'),
              // Fallback to any voice
              () => true
            ];

            for (const priority of voicePriorities) {
              selectedVoice = voices.find(priority) || null;
              if (selectedVoice) break;
            }
          }

          if (selectedVoice) {
            utterance.voice = selectedVoice;
            console.log('Using voice:', selectedVoice.name, selectedVoice.lang, 'Local:', selectedVoice.localService);
          } else {
            console.warn('No suitable voice found, using default');
          }

          // FIXED: Conservative settings to prevent Chrome issues
          utterance.rate = 0.8; // Slower, more reliable rate
          utterance.pitch = 1.0;
          utterance.volume = 0.7;
          utterance.lang = 'en-US';

          let hasStarted = false;
          let hasEnded = false;

          // FIXED: Comprehensive error handling
          utterance.onstart = () => {
            hasStarted = true;
            console.log('Speech synthesis started successfully');
          };

          utterance.onend = () => {
            if (!hasEnded) {
              hasEnded = true;
              console.log('Speech synthesis completed');
              this.currentUtterance = undefined;
              resolve("speech-completed");
            }
          };

          utterance.onerror = (event) => {
            console.error('Speech synthesis error:', event.error);

            if (!hasEnded) {
              hasEnded = true;
              this.currentUtterance = undefined;

              // FIXED: Specific error handling for different Chrome issues
              if (event.error === 'synthesis-failed' ||
                event.error === 'audio-busy' ||
                event.error === 'audio-hardware') {

                console.log('Attempting fallback speech synthesis...');

                // Try minimal utterance as fallback
                setTimeout(() => {
                  try {
                    this.synthesis.cancel();

                    const fallbackUtterance = new SpeechSynthesisUtterance(
                      cleanText.length > 100 ? cleanText.substring(0, 100) + '...' : cleanText
                    );

                    // Use absolutely minimal settings
                    fallbackUtterance.rate = 1.0;
                    fallbackUtterance.pitch = 1.0;
                    fallbackUtterance.volume = 0.5;
                    fallbackUtterance.lang = 'en-US';
                    // Don't set voice - use browser default

                    fallbackUtterance.onend = () => resolve("speech-completed-fallback");
                    fallbackUtterance.onerror = () => resolve("speech-error-final");

                    this.synthesis.speak(fallbackUtterance);
                  } catch (e) {
                    console.error('Fallback speech also failed:', e);
                    resolve("speech-error-final");
                  }
                }, 200);

              } else {
                resolve("speech-error");
              }
            }
          };

          // FIXED: Enhanced Chrome compatibility sequence
          try {
            // Force cancel any existing speech
            this.synthesis.cancel();

            // Wait a bit for cancellation to complete
            setTimeout(() => {
              try {
                // Check if synthesis is available
                if (this.synthesis.paused) {
                  this.synthesis.resume();
                }

                // Store current utterance for potential cancellation
                this.currentUtterance = utterance;

                // Start speaking
                this.synthesis.speak(utterance);

                // Safety timeout - if speech doesn't start within 3 seconds, consider it failed
                setTimeout(() => {
                  if (!hasStarted && !hasEnded) {
                    console.warn('Speech synthesis timeout - forcing end');
                    hasEnded = true;
                    this.currentUtterance = undefined;
                    this.synthesis.cancel();
                    resolve("speech-timeout");
                  }
                }, 3000);

              } catch (speakError) {
                console.error('Error during speak():', speakError);
                resolve("speech-error");
              }
            }, 100);

          } catch (error) {
            console.error('Error setting up speech synthesis:', error);
            resolve("speech-error");
          }
        };

        // Start the voice loading process
        waitForVoices();
      };

      // FIXED: Ensure we're in a user interaction context
      if (document.visibilityState === 'visible') {
        initializeSpeech();
      } else {
        // If page is not visible, wait for it to become visible
        const handleVisibilityChange = () => {
          if (document.visibilityState === 'visible') {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            initializeSpeech();
          }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Fallback timeout
        setTimeout(() => {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          resolve("speech-visibility-timeout");
        }, 5000);
      }
    });
  }

  stopSpeech() {
    if (this.synthesis) {
      this.synthesis.cancel();
    }
    this.currentUtterance = undefined;
  }

  playAudioFromBase64(base64Data: string, mimeType: string = "audio/wav") {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const audioUrl = URL.createObjectURL(blob);

      const audio = new Audio(audioUrl);
      audio.play();

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  }

  // Voice detection utilities
  async setupVoiceDetection(): Promise<MediaStream | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      return stream;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      return null;
    }
  }

  createMediaRecorder(stream: MediaStream, onDataAvailable: (blob: Blob) => void): MediaRecorder {
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus'
    });

    const audioChunks: BlobPart[] = [];

    mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    });

    mediaRecorder.addEventListener('stop', () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      onDataAvailable(audioBlob);
      audioChunks.length = 0;
    });

    return mediaRecorder;
  }

  // Audio level monitoring for visualizer
  createAudioAnalyzer(stream: MediaStream): AnalyserNode | null {
    try {
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContextClass();
      }

      const source = this.audioContext.createMediaStreamSource(stream);
      const analyzer = this.audioContext.createAnalyser();

      analyzer.fftSize = 256;
      analyzer.smoothingTimeConstant = 0.8;

      source.connect(analyzer);

      return analyzer;
    } catch (error) {
      console.error('Error creating audio analyzer:', error);
      return null;
    }
  }

  getAudioLevels(analyzer: AnalyserNode): number[] {
    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyzer.getByteFrequencyData(dataArray);

    // Convert to normalized values (0-1)
    return Array.from(dataArray).map(value => value / 255);
  }

  // Cleanup methods
  cleanup() {
    this.stopSpeech();
    this.disconnectLiveAPI();

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

export default VoiceAIService;